import VariableDrawer from '@/components/variable-insertion-panel';
import { Modal, ModalBody, ModalContent, ModalHeader } from '@nextui-org/react';
import { Extension, InputRule } from '@tiptap/core';
import Bold from '@tiptap/extension-bold';
import Code from '@tiptap/extension-code';
import Document from '@tiptap/extension-document';
import History from '@tiptap/extension-history';
import Italic from '@tiptap/extension-italic';
import Text from '@tiptap/extension-text';
import Underline from '@tiptap/extension-underline';
import { Fragment, MarkType, Schema, Slice } from '@tiptap/pm/model';
import { EditorState, Plugin, PluginKey, Selection } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { Editor, EditorContent, useEditor } from '@tiptap/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { htmlToMarkdown, removeInvisibleCharacters } from './html-to-markdown';
import { normalizePastedMarkdown } from './paste-markdown';
import { PROMPT_BLOCK_EXTENSIONS } from './prompt-blocks';
import { parseMarkdown, serializeMarkdown } from './prompt-markdown';
import { PROMPT_TABLE_EXTENSIONS, PROMPT_TABLE_GROUP } from './prompt-table';
import PromptToolbar from './prompt-toolbar';

// Utility function to extract variables from content
const extractVariablesFromContent = (
  content: string,
  allVariables: Record<string, string> = {}
): Record<string, string> => {
  // First, ensure content has properly formatted variables
  const cleanedContent = content.replace(/\{\{\s*([^{}]+)\s*\}\}/g, '{{$1}}');
  const extractedVariables = cleanedContent.match(/\{\{[^{}]+\}\}/g) || [];

  const currentVariableNames = new Set(
    extractedVariables
      .map((variable) => {
        const match = variable.match(/^\{\{([^{}]+)\}\}$/);
        return match ? match[1].trim() : null; // Trim whitespace from variable names
      })
      .filter(Boolean)
  );

  const newVariables = Object.entries(allVariables).reduce(
    (acc, [key, value]) => {
      if (currentVariableNames.has(key)) {
        acc[key] = value;
      }
      return acc;
    },
    {} as Record<string, string>
  );

  extractedVariables.forEach((variable) => {
    const match = variable.match(/^\{\{([^{}]+)\}\}$/);
    if (match) {
      const [, varName] = match;
      const cleanVarName = varName.trim(); // Trim whitespace
      newVariables[cleanVarName] = newVariables[cleanVarName] || '';
    }
  });

  return newVariables;
};

// The stored prompt is Markdown (see prompt-markdown.ts); the editor shows it
// formatted and writes Markdown back on every change.
const getPrompt = (editor: Editor) => serializeMarkdown(editor.state.doc);

const CELL_NODES = new Set(['tableCell', 'tableHeader']);

// The whole selection is inside one table cell (not e.g. Select all that
// happens to start in a cell)
const isInTableCell = (state: EditorState) => {
  const { $from, to } = state.selection;
  for (let depth = $from.depth; depth > 0; depth -= 1) {
    if (CELL_NODES.has($from.node(depth).type.name)) {
      return to <= $from.end(depth);
    }
  }
  return false;
};

// A table cell holds one line: pasted lines are joined with spaces, keeping
// inline formatting but no headings, lists or tables
const cellSlice = (schema: Schema, markdown: string): Slice => {
  const joined = markdown
    .split('\n')
    .map((part) => part.trim())
    .filter(Boolean)
    .join(' ');
  // Keep a space the paste starts or ends with, so it doesn't glue onto the
  // text around the cursor
  const line = `${/^\s/.test(markdown) ? ' ' : ''}${joined}${
    joined && /\s$/.test(markdown) ? ' ' : ''
  }`;
  const doc = schema.nodeFromJSON(parseMarkdown(line, schema));
  const paragraph =
    doc.childCount === 1 && doc.firstChild?.type.name === 'paragraph'
      ? doc.firstChild
      : schema.nodes.paragraph.create(null, line ? schema.text(line) : null);
  return new Slice(Fragment.from(paragraph), 1, 1);
};

/**
 * The top-level blocks the selection fully covers, if it covers whole blocks
 * (e.g. Select all, or the cursor in an empty line). Pasting there replaces
 * those blocks, instead of pasting into the first one, which would put a
 * pasted list inside a list item or a table after an empty line.
 */
const wholeBlocksRange = (state: EditorState) => {
  const { $from, $to } = state.selection;
  if ($from.depth === 0 || $to.depth === 0) return null;
  const from = $from.before(1);
  const to = $to.after(1);
  const before = state.doc.textBetween(from, $from.pos, '\n', '\n');
  const after = state.doc.textBetween($to.pos, to, '\n', '\n');
  return before || after ? null : { from, to };
};

const LIST_NODES = new Set(['bulletList', 'orderedList']);

/**
 * Where pasted list items go when the cursor is at the end of an item in a
 * list of the same type: right after that item, as siblings (as in Docs and
 * Word), instead of nested inside it. Null for any other paste.
 */
const listSiblingInsertPos = (state: EditorState, slice: Slice) => {
  const { $from, empty } = state.selection;
  const pasted = slice.content;
  if (!empty || pasted.childCount !== 1) return null;
  const list = pasted.firstChild!;
  if (!LIST_NODES.has(list.type.name)) return null;
  for (let depth = $from.depth; depth > 1; depth -= 1) {
    if ($from.node(depth).type.name !== 'listItem') continue;
    const atItemEnd =
      $from.parent.type.name === 'paragraph' &&
      $from.parentOffset === $from.parent.content.size &&
      $from.node(depth).lastChild === $from.parent;
    const sameList = $from.node(depth - 1).type === list.type;
    return atItemEnd && sameList ? $from.after(depth) : null;
  }
  return null;
};

const markdownToSlice = (
  schema: Schema,
  markdown: string,
  isNewContent: boolean
): Slice => {
  const json = parseMarkdown(markdown, schema);
  // Content pasted from outside is new: it gets blank lines between blocks
  // when saved, whatever spacing the pasted text had. A copy from this editor
  // keeps its stored spacing, so it pastes exactly as copied.
  if (isNewContent) {
    json.content?.forEach((block) => {
      if (block.attrs) block.attrs.joinedTo = null;
    });
  }
  const doc = schema.nodeFromJSON(json);
  // Only a plain paragraph joins the line at the cursor; a pasted heading (or
  // list, quote, table) stays a block of its own instead of being merged into
  // the current line and losing its formatting
  const opens = (node: typeof doc.firstChild) =>
    node?.type.name === 'paragraph';
  return new Slice(
    doc.content,
    opens(doc.firstChild) ? 1 : 0,
    opens(doc.lastChild) ? 1 : 0
  );
};

// Function to clean up orphaned variables
const cleanupOrphanedVariables = (
  content: string,
  variables: Record<string, string>
): Record<string, string> => {
  // First, ensure content has properly formatted variables
  const cleanedContent = content.replace(/\{\{\s*([^{}]+)\s*\}\}/g, '{{$1}}');
  const variableMatches = cleanedContent.match(/\{\{[^{}]+\}\}/g) || [];
  const currentVariableNames = new Set(
    variableMatches
      .map((variable) => {
        const match = variable.match(/^\{\{([^{}]+)\}\}$/);
        return match ? match[1].trim() : null; // Trim whitespace
      })
      .filter(Boolean)
  );

  const cleanedVariables: Record<string, string> = {};
  Object.entries(variables).forEach(([key, value]) => {
    if (currentVariableNames.has(key)) {
      cleanedVariables[key] = value;
    }
  });

  return cleanedVariables;
};

interface PromptEditorProps {
  initialName?: string;
  initialVersion?: string;
  onSave?: (content: string, variables: Record<string, string>) => void;
  onImport?: (content: string) => void;
  initialContent?: string;
  onContentChange?: (
    content: string,
    variables: Record<string, string>
  ) => void;
  variables?: Record<string, string>;
  isInvalid?: boolean;
  errorMessage?: string;
}

const VariableHighlight = Extension.create({
  name: 'variableHighlight',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('variableHighlight'),
        props: {
          decorations: (state) => {
            const { doc } = state;
            const decorations: Decoration[] = [];

            doc.descendants((node, pos) => {
              if (!node.text) return;

              let match;
              const regex = /\{\{([^}]+)\}\}/g;

              while ((match = regex.exec(node.text)) !== null) {
                const fullMatch = match[0];
                const variableName = match[1];

                const start = pos + match.index;
                const end = start + fullMatch.length;

                // Simple variable highlighting
                decorations.push(
                  Decoration.inline(start, end, {
                    class: 'variable-highlight',
                    'data-variable': variableName,
                    style:
                      'background-color: #fef3c7; border-radius: 2px; padding: 1px 2px; border: 1px solid #f59e0b; cursor: pointer; font-family: "Courier New", monospace; font-size: 0.9em; color: #92400e; transition: all 0.2s ease;',
                  })
                );
              }
            });

            return DecorationSet.create(doc, decorations);
          },
        },
      }),
    ];
  },
});

// Typed Markdown turns into formatting as soon as it is complete ("**bold**"
// becomes bold), using the same patterns the loader uses, so the editor shows
// exactly what the saved prompt will show after a reload. TipTap's default
// rules are looser (they turn "2 * 3 *" into italics), so they are replaced.
// Block shortcuts such as "# " or "- " at the start of a line are TipTap's.
//
// Each pattern captures the character before the opening marker in group 1 and
// the text to format in group 2 (no lookbehind: Safari < 16.4 can't parse it,
// which would stop the whole app bundle from loading).
const promptMarkRule = (
  find: RegExp,
  type: MarkType,
  open: number,
  attrs?: Record<string, unknown>
) =>
  new InputRule({
    find,
    handler: ({ state, range, match }) => {
      const text = match[2];
      if (!text) return null;
      const start = range.from + (match[1]?.length ?? 0);
      const textStart = start + open;
      const textEnd = textStart + text.length;
      const { tr } = state;
      // The closing marker's last character is the one being typed and is
      // never inserted; remove the rest of it, then the opening marker
      if (textEnd < range.to) tr.delete(textEnd, range.to);
      tr.delete(start, textStart);
      tr.addMark(start, start + text.length, type.create(attrs));
      tr.removeStoredMark(type);
    },
  });

const noPasteRules = {
  addPasteRules() {
    return [];
  },
};

const PromptBold = Bold.extend({
  ...noPasteRules,
  addInputRules() {
    return [
      promptMarkRule(/()\*\*([^\s*](?:[^*]*[^\s*])?)\*\*$/, this.type, 2),
    ];
  },
});

const PromptItalic = Italic.extend({
  ...noPasteRules,
  // Which Markdown marker the italic came from ("*" or "_"), so it is saved
  // back exactly as typed or loaded
  addAttributes() {
    return { marker: { default: null, rendered: false } };
  },
  addInputRules() {
    return [
      promptMarkRule(/(^|[^*\w])\*([^\s*](?:[^*]*[^\s*])?)\*$/, this.type, 1, {
        marker: '*',
      }),
      promptMarkRule(/(^|[^_\w])_([^\s_](?:[^_]*[^\s_])?)_$/, this.type, 1, {
        marker: '_',
      }),
    ];
  },
});

const PromptUnderline = Underline.extend({
  addInputRules() {
    return [promptMarkRule(/()<u>([^<>]+?)<\/u>$/, this.type, 3)];
  },
});

const PromptCode = Code.extend({
  ...noPasteRules,
  addInputRules() {
    return [promptMarkRule(/(^|[^`])`([^`]+)`$/, this.type, 1)];
  },
});

// Heading, list, inline code and table styles for the prompt, overriding the global
// block-editor styles (src/editor/styles/partials) only inside this editor.
// Sizes are ~25% smaller than those styles (text-3xl / 2xl / xl).
const PROMPT_CONTENT_STYLES = [
  '[&_.ProseMirror_:is(h1,h2,h3)]:!mt-4',
  '[&_.ProseMirror_:is(h1,h2,h3)]:!mb-1',
  '[&_.ProseMirror_:is(h1,h2,h3)]:!leading-snug',
  '[&_.ProseMirror_h1]:!text-[1.375rem]',
  '[&_.ProseMirror_h2]:!text-lg',
  '[&_.ProseMirror_h3]:!text-base',
  '[&_.ProseMirror>:is(ul,ol)]:!mt-2',
  '[&_.ProseMirror>:is(ul,ol)]:!mb-4',
  // Inline code as a light grey chip instead of the global white-on-black
  '[&_.ProseMirror_code]:!bg-neutral-100',
  '[&_.ProseMirror_code]:!text-neutral-800',
  '[&_.ProseMirror_code]:!caret-neutral-800',
  '[&_.ProseMirror_code]:!border',
  '[&_.ProseMirror_code]:!border-neutral-200',
  '[&_.ProseMirror_code]:!rounded',
  '[&_.ProseMirror_code]:!shadow-none',
  '[&_.ProseMirror_code]:!px-1',
  '[&_.ProseMirror_code]:!text-[0.9em]',
  '[&_.ProseMirror_code]:selection:!bg-blue-200',
  'dark:[&_.ProseMirror_code]:!bg-neutral-800',
  'dark:[&_.ProseMirror_code]:!text-neutral-100',
  'dark:[&_.ProseMirror_code]:!caret-neutral-100',
  'dark:[&_.ProseMirror_code]:!border-neutral-700',
  'dark:[&_.ProseMirror_code]:selection:!bg-blue-800',
  // Tables: compact cells, grey header row
  '[&_.ProseMirror_table]:!my-3',
  '[&_.ProseMirror_table]:!text-[inherit]',
  '[&_.ProseMirror_:is(td,th)]:!px-2',
  '[&_.ProseMirror_:is(td,th)]:!py-1',
  '[&_.ProseMirror_:is(td,th)]:!min-w-[80px]',
  '[&_.ProseMirror_:is(td,th)]:!border-neutral-300',
  '[&_.ProseMirror_:is(td,th)_p]:!m-0',
  '[&_.ProseMirror_th]:!bg-neutral-100',
  '[&_.ProseMirror_th]:!text-left',
  'dark:[&_.ProseMirror_:is(td,th)]:!border-neutral-600',
  'dark:[&_.ProseMirror_th]:!bg-neutral-800',
].join(' ');

const TABLE_NODES = new Set(['table', 'tableRow', 'tableHeader', 'tableCell']);

// Copying text inside one table cell gives a slice wrapped in its table, row
// and cell; unwrap it so the clipboard gets the text, not a one-cell table
const unwrapTableSlice = (slice: Slice): Fragment => {
  let content = slice.content;
  let open = slice.openStart;
  while (
    open > 0 &&
    content.childCount === 1 &&
    TABLE_NODES.has(content.firstChild!.type.name)
  ) {
    content = content.firstChild!.content;
    open -= 1;
  }
  return content;
};

const PROMPT_EXTENSIONS = [
  // Tables only at the top level of the prompt (see prompt-table.ts)
  Document.extend({ content: `(block | ${PROMPT_TABLE_GROUP})+` }),
  ...PROMPT_BLOCK_EXTENSIONS,
  Text,
  PromptBold,
  PromptItalic,
  PromptUnderline,
  PromptCode,
  ...PROMPT_TABLE_EXTENSIONS,
  History.configure({ depth: 100, newGroupDelay: 500 }),
];

export default function PromptEditor({
  initialContent = '',
  onContentChange,
  variables = {},
  isInvalid = false,
  errorMessage,
}: PromptEditorProps) {
  const [isVariablePanelOpen, setIsVariablePanelOpen] = useState(false);
  const [selectedVariable, setSelectedVariable] = useState('');
  const [isMaximized, setIsMaximized] = useState(false);

  // Memoize variables to prevent unnecessary re-renders
  const memoizedVariables = useMemo(
    () => variables,
    [JSON.stringify(variables)]
  );

  // Add minimal CSS for variable highlighting
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .variable-highlight {
        background-color: #fef3c7 !important;
        border-radius: 2px !important;
        padding: 1px 2px !important;
        border: 1px solid #f59e0b !important;
        cursor: pointer !important;
        font-family: 'Courier New', monospace !important;
        font-size: 0.9em !important;
        color: #92400e !important;
        transition: all 0.2s ease !important;
        user-select: text !important;
        pointer-events: auto !important;
      }
      .variable-highlight:hover {
        background-color: #fde68a !important;
        border-color: #d97706 !important;
        color: #78350f !important;
        transform: translateY(-1px) !important;
        box-shadow: 0 2px 4px rgba(245, 158, 11, 0.2) !important;
      }
      .variable-highlight:active {
        background-color: #fbbf24 !important;
        color: #451a03 !important;
        transform: translateY(0) !important;
      }
    `;
    document.head.appendChild(style);
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  const editor = useEditor({
    extensions: [...PROMPT_EXTENSIONS, VariableHighlight],
    editorProps: {
      attributes: {
        class:
          'prose prose-sm sm:prose lg:prose-sm xl:prose-md dark:prose-invert !max-w-none focus:outline-none w-full h-full min-h-[200px] whitespace-pre-wrap prose-headings:mt-3 prose-headings:mb-1 prose-p:my-0 prose-ul:my-1 prose-ol:my-1 prose-li:my-0 prose-blockquote:my-1 prose-blockquote:font-normal prose-blockquote:not-italic prose-code:before:content-none prose-code:after:content-none [&_blockquote_p]:before:content-none [&_blockquote_p]:after:content-none',
      },
      // Pasted HTML is never inserted directly. Formatted content from Word,
      // Google Docs or web pages is converted to Markdown first; copies from
      // this editor and plain text are already Markdown.
      handlePaste: (view, event) => {
        const text = event.clipboardData?.getData('text/plain') ?? '';
        const html = event.clipboardData?.getData('text/html') ?? '';
        // Copies from this editor are pasted exactly as copied
        const fromThisEditor = html.includes('data-pm-slice');
        const markdown = html && !fromThisEditor ? htmlToMarkdown(html) : null;
        const pasted =
          markdown ?? (fromThisEditor ? text : normalizePastedMarkdown(text));
        const content = removeInvisibleCharacters(pasted);
        if (!content && !html) return false;
        const inCell = isInTableCell(view.state);
        const blocks = inCell ? null : wholeBlocksRange(view.state);
        const slice = inCell
          ? cellSlice(view.state.schema, content)
          : markdownToSlice(view.state.schema, content, !fromThisEditor);
        const tr = view.state.tr;
        const siblingPos =
          inCell || blocks ? null : listSiblingInsertPos(view.state, slice);
        if (siblingPos !== null) {
          const items = slice.content.firstChild!.content;
          tr.insert(siblingPos, items);
          tr.setSelection(
            Selection.near(tr.doc.resolve(siblingPos + items.size), -1)
          );
        } else if (blocks) {
          tr.replaceRange(blocks.from, blocks.to, slice);
          const end = tr.mapping.map(blocks.to, -1);
          tr.setSelection(Selection.near(tr.doc.resolve(end), -1));
        } else {
          tr.replaceSelection(slice);
        }
        view.dispatch(tr.scrollIntoView());
        return true;
      },
      // Copy as the same Markdown the prompt is saved as
      clipboardTextSerializer: (slice, view) => {
        try {
          return serializeMarkdown(
            view.state.schema.topNodeType.create(null, unwrapTableSlice(slice))
          );
        } catch {
          return slice.content.textBetween(0, slice.content.size, '\n');
        }
      },
    },
    onUpdate: ({ editor }) => {
      const text = getPrompt(editor);

      const cleanedVariables = cleanupOrphanedVariables(
        text,
        memoizedVariables
      );
      const newVariables = extractVariablesFromContent(text, cleanedVariables);

      onContentChange?.(text, newVariables);
    },
  });

  const focusEditor = useCallback(() => {
    editor?.chain().focus().run();
  }, [editor]);

  // Only replace the document when the value comes from outside the editor
  // (e.g. the prompt loaded from the API). Echoes of what the user just typed
  // must not reset the document, or the cursor and in-progress input are lost.
  useEffect(() => {
    if (!editor) return;

    const nextContent = initialContent ?? '';
    if (nextContent === getPrompt(editor)) return;

    // Loading a prompt is not an undoable edit
    editor
      .chain()
      .setMeta('addToHistory', false)
      .setContent(parseMarkdown(nextContent, editor.schema), false)
      .run();
  }, [editor, initialContent]);

  useEffect(() => {
    if (editor) {
      const handleClick = (e: MouseEvent) => {
        const target = e.target as HTMLElement;

        // Handle clicks on variable highlights
        if (target.classList.contains('variable-highlight')) {
          e.preventDefault();
          e.stopPropagation();

          const variableName = target.getAttribute('data-variable');

          if (variableName) {
            // Open variable drawer for this variable
            setSelectedVariable(variableName);
            setIsVariablePanelOpen(true);
          }
        }
      };

      editor.view.dom.addEventListener('click', handleClick);

      return () => {
        editor.view.dom.removeEventListener('click', handleClick);
      };
    }
  }, [editor]);

  const updateVariableValue = useCallback(
    (variableName: string, value: string) => {
      if (editor && variableName) {
        // Get current content and clean up orphaned variables
        const currentContent = getPrompt(editor);
        const cleanedVariables = cleanupOrphanedVariables(
          currentContent,
          memoizedVariables
        );

        // Update the variables object with the new value
        const updatedVariables = {
          ...cleanedVariables,
          [variableName]: value,
        };

        // Notify parent of content change
        onContentChange?.(currentContent, updatedVariables);
      }
    },
    [editor, memoizedVariables, onContentChange]
  );

  // const deleteVariable = useCallback(
  //   (variableName: string) => {
  //     if (editor && variableName) {
  //       const content = editor.getHTML();

  //       // Remove all occurrences of the variable
  //       const escapedName = variableName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  //       const newContent = content.replace(
  //         new RegExp(`\\{\\{${escapedName}\\}\\}`, 'g'),
  //         ''
  //       );

  //       // Clean up variables object
  //       const cleanedVariables = cleanupOrphanedVariables(
  //         newContent,
  //         memoizedVariables
  //       );

  //       // Update editor content
  //       editor.commands.setContent(newContent);

  //       // Get clean content for saving
  //       const cleanContent = getCleanContentForSaving(newContent);
  //       onContentChange?.(cleanContent, cleanedVariables);
  //     }
  //   },
  //   [editor, memoizedVariables, onContentChange]
  // );

  return (
    <div className="container mx-auto py-4 max-w-full text-sm">
      <div className="space-y-4">
        <div
          className={`border rounded-md p-2 !min-h-[200px] cursor-text ${
            isInvalid ? 'border-danger' : ''
          }`}
          onClick={focusEditor}
        >
          <div className="flex flex-wrap items-center justify-between gap-2 px-2 pb-1 border-b border-divider">
            <label
              htmlFor="prompt"
              className={`block text-sm font-medium ${
                isInvalid ? 'text-danger' : 'text-gray-700'
              }`}
            >
              PROMPT
            </label>
            <PromptToolbar editor={editor} />
          </div>
          <EditorContent
            editor={editor}
            className={`relative [&_.ProseMirror]:min-h-[200px] [&_.ProseMirror]:!max-h-[50vh] [&_.ProseMirror]:overflow-y-auto [&_.ProseMirror]:w-full [&_.ProseMirror]:p-2 [&_.ProseMirror]:m-0 [&_.ProseMirror]:leading-tight [&_.ProseMirror>p]:m-0 ${PROMPT_CONTENT_STYLES}`}
          />
          {errorMessage && (
            <p className="px-2 pt-1 text-tiny text-danger">{errorMessage}</p>
          )}
        </div>

        {/* Maximized Modal */}
        {isMaximized && (
          <Modal
            isOpen={isMaximized}
            onClose={() => setIsMaximized(false)}
            size="5xl"
            classNames={{
              base: 'h-[90vh] w-full',
              body: 'm-8',
              closeButton: 'hover:bg-gray-100 dark:hover:bg-gray-700',
            }}
          >
            <ModalContent>
              <ModalHeader className="border-b">
                <h2 className="text-lg font-semibold">Editor</h2>
              </ModalHeader>
              <ModalBody className="overflow-hidden">
                <EditorContent
                  editor={editor}
                  className={`h-full [&_.ProseMirror]:min-h-[calc(90vh-8rem)] [&_.ProseMirror]:max-h-[calc(90vh-8rem)] [&_.ProseMirror]:p-0 [&_.ProseMirror]:overflow-y-auto [&_.ProseMirror]:m-0 [&_.ProseMirror]:leading-tight [&_.ProseMirror>p]:m-0 ${PROMPT_CONTENT_STYLES}`}
                />
              </ModalBody>
            </ModalContent>
          </Modal>
        )}

        {/* Variable Panel */}
        {isVariablePanelOpen && (
          <VariableDrawer
            isOpen={isVariablePanelOpen}
            onClose={() => setIsVariablePanelOpen(false)}
            variables={(() => {
              const content = editor ? getPrompt(editor) : '';
              const variableNames = extractVariablesFromContent(content, {});

              const panelVariables: Record<string, string> = {};

              Object.keys(variableNames).forEach((varName) => {
                panelVariables[varName] = memoizedVariables[varName] || '';
              });

              if (selectedVariable && !panelVariables[selectedVariable]) {
                panelVariables[selectedVariable] =
                  memoizedVariables[selectedVariable] || '';
              }

              return panelVariables;
            })()}
            onSubmit={(newVariables) => {
              const value = newVariables[selectedVariable];
              updateVariableValue(selectedVariable, value);
              setIsVariablePanelOpen(false);
            }}
            selectedVariable={selectedVariable}
          />
        )}
      </div>
    </div>
  );
}
